import { useCallback, useEffect, useRef, useState } from "react";
import { TbSearch } from "react-icons/tb";
import InputMask from "react-input-mask";
import { boleto } from "boleto-brasileiro-validator";
import moment from "moment";
import { Button } from "../../../Components/Atoms";
import {
  PageHeaderWithBack,
  PasswordConfirm,
  PaymentBarcodeSkeleton,
  PaymentConfirmSkeleton,
  PaymentsShortcutMenu,
  ScreenGreeting,
} from "../../../Components/Molecules";
import BarcodeDataDetails from "../../../Components/Molecules/BarcodeDataDetails";
import {
  useAccountBalanceData,
  useBarcodePayment,
  useConsultBarCodeData,
} from "../../../Hooks/index";
import { isFidoStepUpPassword } from "../../../Hooks/useFidoStepUp";
import { receiptBankMask, receiptChargeMask } from "../../../Utils/masks";
import { FinalStepPayment } from "./paymentFinalStep";

export const Payments = () => {
  const barcodeInputRef = useRef(null);
  const [receiptCode, setReceiptCode] = useState();
  const [requirePassword, setRequirePassword] = useState(false);
  const [step, setStep] = useState(0);
  const [isReceiptErrored, setIsReceiptErrored] = useState(false);
  const {
    handleGET: fetchConsultBarCodeData,
    loading: loadingBarCodeDetails,
    state: barCodeDetails,
  } = useConsultBarCodeData();

  const {
    success: paymentStatus,
    handlePOST: fetchBarcodePayment,
    loading: paymentLoading,
    state: paymentState,
  } = useBarcodePayment();

  const { handleGET: fetchAccountBalanceGET } = useAccountBalanceData();

  const handlePayment = useCallback(() => {
    setRequirePassword(true);
  }, []);

  const handleFinishTransaction = useCallback(
    (pass) => {
      setRequirePassword(false);
      if (!isFidoStepUpPassword(pass) && pass.length !== 4) return;

      let barcode_line = receiptCode.replace(/\./g, "");
      barcode_line = barcode_line.replace("-", "");
      barcode_line = barcode_line.replace(/\s/g, "");

      fetchBarcodePayment({
        validation: {
          password_combination: pass,
        },
        scheduled_to: moment().format("YYYY-MM-DD"),
        payment_mode: {
          type: "account",
        },
        barcode_line,
      });
    },
    [receiptCode, fetchBarcodePayment]
  );

  const handleSubmit = useCallback(() => {
    if (!receiptCode) {
      setIsReceiptErrored(true);
      return;
    }
    if (!boleto(receiptCode)) {
      setIsReceiptErrored(true);
      return;
    }

    let barcode_line = receiptCode.replace(/\./g, "");
    barcode_line = barcode_line.replace("-", "");
    barcode_line = barcode_line.replace(/\s/g, "");

    fetchConsultBarCodeData({
      barcode_line,
    });
    fetchAccountBalanceGET();
  }, [receiptCode, fetchConsultBarCodeData, fetchAccountBalanceGET]);

  const resetToHome = useCallback(() => {
    setReceiptCode("");
    setStep(0);
  }, []);

  const focusBarcodeInput = useCallback(() => {
    barcodeInputRef.current?.focus();
    barcodeInputRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, []);

  useEffect(() => {
    !!barCodeDetails && !loadingBarCodeDetails.GET && setStep(1);
  }, [barCodeDetails, loadingBarCodeDetails]);

  useEffect(() => {
    paymentStatus.POST === true && !paymentLoading.POST && setStep(2);
  }, [paymentStatus, paymentLoading]);

  return (
    <div className="w-full">
      {step === 0 && (
        <>
          <ScreenGreeting title="Pagamentos" />

          <PaymentsShortcutMenu onFocusBarcode={focusBarcodeInput} />

          <section className="mb-5">
            <h2 className="mb-3 text-sm font-bold text-brand-dark">Código do boleto</h2>
            <div className="app-card p-4 md:p-5">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-stretch">
                <div className="min-w-0 flex-1">
                  <InputMask
                    inputRef={barcodeInputRef}
                    className={`valut-input w-full rounded-lg px-4 py-3 text-base text-brand-dark placeholder:text-gray-400 md:text-lg ${
                      isReceiptErrored ? "border-red-500" : ""
                    }`}
                    mask={
                      receiptCode?.substring(0, 1) === "8"
                        ? receiptChargeMask
                        : receiptBankMask
                    }
                    value={receiptCode}
                    onChange={(e) => {
                      setReceiptCode(e.target.value);
                    }}
                    onFocus={() => {
                      setIsReceiptErrored(false);
                    }}
                    placeholder="Cole ou digite o código"
                    maskChar={null}
                  />
                </div>
                <Button
                  className="shrink-0 sm:w-[4.125rem]"
                  style={{ width: "100%", height: 52 }}
                  onClick={handleSubmit}
                  loading={loadingBarCodeDetails.GET}
                  aria-label="Consultar boleto"
                >
                  <TbSearch className="h-6 w-6" />
                </Button>
              </div>

              {isReceiptErrored && (
                <p className="mt-2 text-xs text-red-600">
                  Erro no código do boleto. Revise o código digitado.
                </p>
              )}
            </div>
          </section>

          <section className="mb-5">
            <h2 className="mb-3 text-sm font-bold text-brand-dark">Forma de pagamento</h2>
            <div className="app-card p-4 md:p-5">
              <label className="pix-transfer-radio pix-transfer-radio--active flex w-full items-center justify-between gap-3 rounded-xl border border-gray-100 px-4 py-4">
                <span className="flex items-center gap-2">
                  <input type="radio" name="paymentType" value="balance" defaultChecked readOnly />
                  Saldo em conta
                </span>
              </label>
            </div>
          </section>

          {loadingBarCodeDetails.GET && <PaymentBarcodeSkeleton />}
        </>
      )}

      {step === 1 && (
        <>
          <PageHeaderWithBack
            title="Finalizar pagamento"
            subTitle="Confira os detalhes da cobrança antes de confirmar a transação."
            onBack={resetToHome}
          />

          {paymentLoading.POST ? (
            <PaymentConfirmSkeleton />
          ) : (
            <>
              <BarcodeDataDetails receipt={barCodeDetails} />
              <div className="mt-5">
                <Button onClick={handlePayment} loading={paymentLoading.POST}>
                  Finalizar pagamento
                </Button>
              </div>
            </>
          )}

          <PasswordConfirm
            submitPassword={(e) => handleFinishTransaction(e)}
            open={requirePassword}
            onClose={() => setRequirePassword(false)}
          />
        </>
      )}

      {step === 2 && (
        <FinalStepPayment
          refreshStep={resetToHome}
          paymentSuccess={paymentStatus.POST}
          paymentData={paymentState}
        />
      )}
    </div>
  );
};
